import { useState, useEffect } from "react";
import {
  Box,
  Autocomplete,
  CircularProgress,
  TextField as MuiTextField,
} from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import DatePicker from "../../shared/DatePicker/index.tsx";
import Switch from "../../shared/Switch/index.tsx";
import AutoCompleteMultiSelect from "../../shared/AutoCompleteMultiSelect/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import dayjs from "dayjs";
import customerTypes from "../../shared/customerTypes.json";
import "../../styles/scss/Form.scss";

export default function WashCreateEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);

  // --- Vehicle autocomplete ---
  const [vehicleOptions, setVehicleOptions] = useState<any[]>([]);
  const [vehicleSearching, setVehicleSearching] = useState(false);
  const [vehicleInput, setVehicleInput] = useState("");
  const [vehicleDetailsNeeded, setVehicleDetailsNeeded] = useState(true);

  // --- Customer autocomplete ---
  const [customerOptions, setCustomerOptions] = useState<any[]>([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [customerInput, setCustomerInput] = useState("");
  const [regularCustomer, setRegularCustomer] = useState(true);
  const [customerOffer, setCustomerOffer] = useState<{
    offerStatus: boolean;
    washCount: number;
    eligibleAt: number;
  } | null>(null);

  const nonCustomerTypes = customerTypes.filter(
    (type) => type.value !== "Customer",
  );

  const checkCustomerOffer = async (customerId: number | null) => {
    setCustomerOffer(null);
    if (!customerId) return;
    try {
      const res = await request.get("api/checkCustomerOffer", { customerId });
      if (res?.data?.data) {
        setCustomerOffer(res.data.data);
      }
    } catch {
      setCustomerOffer(null);
    }
  };

  const fillFromVehicle = (vehicle: any) => {
    if (!vehicle) return;
    const cust = vehicle.customer;
    formik.setValues({
      ...formik.values,
      regNo: vehicle.regNo || "",
      vehicleDetails: vehicle.vehicleDetails || "",
      customerId: cust?.id || null,
      vehicleId: vehicle.id || null,
      customerName: cust?.name || "",
      customerPhone: cust?.phone || "",
      customerEmail: cust?.email || "",
      customerType: cust?.customerType || "Customer",
    });
    setRegularCustomer(!cust?.customerType || cust.customerType === "Customer");
    setCustomerInput(cust?.name || "");
    checkCustomerOffer(cust?.id || null);
  };

  const fillFromCustomer = (cust: any) => {
    if (!cust) return;
    formik.setValues({
      ...formik.values,
      customerId: cust.id || null,
      customerName: cust.name || "",
      customerPhone: cust.phone || "",
      customerEmail: cust.email || "",
      customerType: cust.customerType || "Customer",
    });
    setRegularCustomer(!cust?.customerType || cust.customerType === "Customer");
    checkCustomerOffer(cust?.id || null);
  };
  useEffect(() => {
    const query = vehicleInput;
    if (!query || query.length < 2) {
      setVehicleOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      setVehicleSearching(true);
      try {
        const res = await request.get("/api/searchVehicle", { regNo: query });
        setVehicleOptions(res.data.data || []);
      } catch {
        setVehicleOptions([]);
      } finally {
        setVehicleSearching(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [vehicleInput]);

  // Debounced customer search — using customerInput (name field)
  useEffect(() => {
    const query = customerInput;
    if (!query || query.length < 2) {
      setCustomerOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      setCustomerSearching(true);
      try {
        const res = await request.get("/api/searchCustomer", {
          phone: query,
          ...(regularCustomer
            ? { customerType: "Customer" }
            : { excludeCustomerType: "Customer" }),
        });
        setCustomerOptions(res.data.data || []);
      } catch {
        setCustomerOptions([]);
      } finally {
        setCustomerSearching(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [customerInput, regularCustomer]);

  const formik = useFormik({
    initialValues: {
      customerId: null as number | null,
      vehicleId: null as number | null,
      customerName: "",
      customerPhone: "",
      customerEmail: "",
      customerType: "Customer",
      regNo: "",
      vehicleDetails: "",
      masterServiceId: [],
      actualAmount: "",
      remarks: "",
      arrivalDate: dayjs().format("YYYY-MM-DD"),
      arrivalTime: dayjs().format("HH:mm"),
    },
    validationSchema: Yup.object({
      customerName: Yup.string().required("Customer name is required"),
      customerType: Yup.string().required("Customer type is required"),
      regNo: Yup.string().when([], {
        is: () => vehicleDetailsNeeded,
        then: (schema) => schema.required("Registration number is required"),
        otherwise: (schema) => schema.nullable(),
      }),
      masterServiceId: Yup.array()
        .min(1, "At least one service is required")
        .required("Service is required"),
      arrivalDate: Yup.string().required("Arrival date is required"),
      arrivalTime: Yup.string().required("Arrival time is required"),
    }),
    onSubmit: async (values) => {
      try {
        const payload = {
          ...values,
          actualAmount: values.actualAmount
            ? parseFloat(values.actualAmount)
            : null,
          customerId: values.customerId || null,
          vehicleId: values.vehicleId || null,
          vehicleDetailsNeeded,
          masterServiceId: Array.isArray(values.masterServiceId)
            ? values.masterServiceId.map((item: any) =>
                typeof item === "object" ? item.id : item,
              )
            : [],
        };
        let url = "/api/createWash";
        let method = "post";
        if (id) {
          url = `/api/updateWash/${id}`;
          method = "put";
        }

        const response = await request[method](url, payload);

        if (response?.data?.status) {
          toast.success(response?.data?.message || "Wash saved successfully!");
          navigate("/admin/washes");
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            "An error occurred. Please try again.",
        );
      }
    },
  });

  useEffect(() => {
    if (id) {
      const fetchWash = async () => {
        setLoading(true);
        try {
          const response = await request.get(`/api/getVehicleWashById/${id}`);
          const wash = response.data.data;
          const name = wash?.vehicle?.customer?.name || "";
          formik.setValues({
            customerId: wash?.vehicle?.customer?.id || null,
            vehicleId: wash?.vehicle?.id || null,
            customerName: wash?.customer?.name || "",
            customerPhone: wash?.customer?.phone || "",
            customerEmail: wash?.customer?.email || "",
            customerType: wash?.customer?.customerType || "Customer",
            regNo: wash?.vehicle?.regNo || "",
            vehicleDetails: wash?.vehicle?.vehicleDetails || "",
            masterServiceId: Array.isArray(wash?.masterServices)
              ? wash.masterServices
              : Array.isArray(wash?.masterServiceId)
                ? wash.masterServiceId
                : [],
            actualAmount: wash?.actualAmount ?? "",
            remarks: wash?.remarks || "",
            arrivalDate: wash?.vehicleArrivedDate || "",
            arrivalTime: wash?.vehicleArrivedTime || "",
          });
          setVehicleInput(wash?.vehicle?.regNo || "");
          setVehicleDetailsNeeded(
            wash?.vehicleDetailsNeeded ?? Boolean(wash?.vehicle?.id),
          );
          setCustomerInput(wash?.customer?.phone || "");
          setRegularCustomer(
            (wash?.customer?.customerType ||
              wash?.vehicle?.customer?.customerType ||
              "Customer") === "Customer",
          );
          checkCustomerOffer(wash?.customer?.id || null);
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      };

      fetchWash();
    }
  }, [id]);

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={id && id !== "new" ? "Edit Wash" : "Create New Wash"}
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/washes"),
              }}
              primaryButton={{
                label: id && id !== "new" ? "Update Wash" : "Create Wash",
                icon: <span className="material-symbols-outlined">save</span>,
                buttonLoading: formik.isSubmitting,
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              {/* Vehicle Details */}
              <Box
                sx={{ fontSize: 13, fontWeight: 700, color: "#1c1b1f", mb: 2 }}
              >
                Vehicle Details
              </Box>

              {/* Row: Vehicle Details Needed switch */}
              <Box
                className="form-grid form-grid--2"
                style={{ marginTop: "16px" }}
              >
                <Box className="form-field">
                  <Switch
                    name="vehicleDetailsNeeded"
                    label="Vehicle Details Needed"
                    checked={vehicleDetailsNeeded}
                    onChange={(checked) => setVehicleDetailsNeeded(checked)}
                  />
                </Box>
              </Box>

              {/* Row 3: RegNo + Vehicle Details */}
              {vehicleDetailsNeeded && (
                <Box
                  className="form-grid form-grid--2"
                  style={{ marginTop: "16px" }}
                >
                  <Box className="form-field">
                    <Autocomplete
                      freeSolo
                      size="small"
                      fullWidth
                      options={vehicleOptions}
                      loading={vehicleSearching}
                      inputValue={vehicleInput}
                      filterOptions={(x) => x}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      getOptionLabel={(option: any) =>
                        typeof option === "string"
                          ? option
                          : `${option.regNo}${option.vehicleDetails ? ` — ${option.vehicleDetails}` : ""}`
                      }
                      onInputChange={(_e, value) => {
                        setVehicleInput(value);
                        formik.setFieldValue("regNo", value);
                        if (!value) {
                          formik.setFieldValue("vehicleId", null);
                        }
                      }}
                      onChange={(_e, value) => {
                        if (typeof value === "object" && value !== null) {
                          fillFromVehicle(value);
                        }
                      }}
                      renderInput={(params) => (
                        <MuiTextField
                          {...params}
                          label="Registration No"
                          name="regNo"
                          error={
                            formik.touched.regNo && Boolean(formik.errors.regNo)
                          }
                          helperText={
                            formik.touched.regNo && formik.errors.regNo
                          }
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {vehicleSearching && (
                                  <CircularProgress size={20} />
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Box>
                  <Box className="form-field">
                    <TextField
                      name="vehicleDetails"
                      label="Vehicle Details"
                      type="text"
                      formik={formik}
                    />
                  </Box>
                </Box>
              )}

              {/* Customer Details */}
              <Box
                sx={{
                  height: "1px",
                  background: "rgba(0,0,0,0.08)",
                  margin: "20px 0",
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box sx={{ fontSize: 13, fontWeight: 700, color: "#1c1b1f" }}>
                  Customer Details
                </Box>
                <Switch
                  name="regularCustomer"
                  label="Regular Customer"
                  checked={regularCustomer}
                  onChange={(checked) => {
                    setRegularCustomer(checked);
                    setCustomerInput("");
                    setCustomerOptions([]);
                    setCustomerOffer(null);
                    formik.setValues({
                      ...formik.values,
                      customerId: null,
                      customerName: "",
                      customerPhone: "",
                      customerEmail: "",
                      customerType: checked
                        ? "Customer"
                        : nonCustomerTypes[0]?.value || "",
                    });
                  }}
                />
              </Box>

              {/* Row: Customer Type + Customer Phone */}
              <Box
                className="form-grid form-grid--2"
                style={{ marginTop: "16px" }}
              >
                <Box className="form-field">
                  <SelectField
                    name="customerType"
                    label="Customer Type"
                    formik={formik}
                    disabled={regularCustomer}
                    options={
                      regularCustomer
                        ? customerTypes.filter(
                            (type) => type.value === "Customer",
                          )
                        : nonCustomerTypes
                    }
                  />
                </Box>
                <Box className="form-field">
                  <Autocomplete
                    freeSolo
                    size="small"
                    fullWidth
                    options={customerOptions}
                    loading={customerSearching}
                    inputValue={customerInput}
                    filterOptions={(x) => x}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    getOptionLabel={(option: any) =>
                      typeof option === "string"
                        ? option
                        : `${option.phone}${option.name ? ` — ${option.name}` : ""}`
                    }
                    onInputChange={(_e, value) => {
                      setCustomerInput(value);
                      formik.setFieldValue("customerPhone", value);
                      if (!value) {
                        formik.setFieldValue("customerId", null);
                      }
                    }}
                    onChange={(_e, value) => {
                      if (typeof value === "object" && value !== null) {
                        fillFromCustomer(value);
                        setCustomerInput(value.phone || "");
                      }
                    }}
                    renderInput={(params) => (
                      <MuiTextField
                        {...params}
                        label="Customer Phone"
                        name="customerPhone"
                        error={
                          formik.touched.customerPhone &&
                          Boolean(formik.errors.customerPhone)
                        }
                        helperText={
                          formik.touched.customerPhone &&
                          formik.errors.customerPhone
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {customerSearching && (
                                <CircularProgress size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>
              </Box>

              {/* Row: Customer Name + Customer Email */}
              <Box
                className="form-grid form-grid--2"
                style={{ marginTop: "16px" }}
              >
                <Box className="form-field">
                  <TextField
                    name="customerName"
                    label="Customer Name"
                    type="text"
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <TextField
                    name="customerEmail"
                    label="Customer Email"
                    type="text"
                    formik={formik}
                  />
                </Box>
              </Box>

              {customerOffer && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: "8px",
                    background: customerOffer.offerStatus
                      ? "rgba(16, 185, 129, 0.08)"
                      : "rgba(245, 158, 11, 0.08)",
                    border: customerOffer.offerStatus
                      ? "1px solid rgba(16, 185, 129, 0.2)"
                      : "1px solid rgba(245, 158, 11, 0.2)",
                    color: customerOffer.offerStatus ? "#065f46" : "#92400e",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  }}
                >
                  {customerOffer.offerStatus
                    ? `🎉 This customer is eligible for ${customerOffer.offerPercentage}% off on this wash!`
                    : `⏳ ${customerOffer.eligibleAt - customerOffer.washCount} more wash${customerOffer.eligibleAt - customerOffer.washCount > 1 ? "es" : ""} to go — unlock an exclusive offer!`}
                </Box>
              )}

              {/* Row 5: Arrival Date + Arrival Time */}
              <Box
                className="form-grid form-grid--2"
                style={{ marginTop: "16px" }}
              >
                <Box className="form-field">
                  <DatePicker
                    name="arrivalDate"
                    label="Arrival Date"
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <TextField
                    name="arrivalTime"
                    label="Arrival Time"
                    type="time"
                    formik={formik}
                  />
                </Box>
              </Box>

              {/* Row 6: Remarks */}
              <Box
                className="form-grid form-grid--2"
                style={{ marginTop: "16px" }}
              >
                <Box className="form-field">
                  <TextField
                    name="remarks"
                    label="Remarks"
                    type="text"
                    formik={formik}
                    multiline
                    rows={3}
                  />
                </Box>
              </Box>

              {/* Row 7: Services */}
              <Box
                sx={{
                  height: "1px",
                  background: "rgba(0,0,0,0.08)",
                  margin: "20px 0",
                }}
              />
              <Box className="form-grid form-grid--2">
                <Box className="form-field">
                  <AutoCompleteMultiSelect
                    name="masterServiceId"
                    label="Select Services"
                    formik={formik}
                    apiUrl="/api/getMasterServices"
                    apiPayload={{
                      orderColumn: "sortOrder",
                      orderDirection: "desc",
                    }}
                    optionLabelKey="serviceName"
                    optionValueKey="id"
                  />
                </Box>
                <Box className="form-field">
                  <TextField
                    name="actualAmount"
                    label="Actual Amount"
                    type="number"
                    formik={formik}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}
